import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Card, CardHeader, CardText } from '@/components/atoms/Card';
import { useGetCageActiveFishGroup } from '@/domain/fishGroups';

import { ActiveFishGroupForm } from './ActiveFishGroupForm';
import { ActiveFishGroupSkeleton } from './ActiveFishGroupSkeleton';

interface Props {
    cageId: number;
    clientId: number;
}

/**
 * The cage settings' card for the fish group in the pen. The page shows it beside the cage's own
 * card, only while fish-group tracking is on and to the admins of the cage's location (client
 * and OptoScale admins included), the same rule the backend applies.
 */
export function ActiveFishGroupSection(props: Props) {
    const { cageId, clientId } = props;
    const { t } = useTranslation('cageSettings');
    const { data: group, isLoading, error } = useGetCageActiveFishGroup(cageId);

    let content: ReactNode;
    if (isLoading) {
        content = <ActiveFishGroupSkeleton />;
    } else if (error) {
        content = (
            <Alert tone="danger" size="small">
                {apiErrorContent(error, undefined, (message) =>
                    t('activeFishGroup.loadError', { message })
                )}
            </Alert>
        );
    } else if (!group) {
        content = <CardText>{t('activeFishGroup.noActiveGroup')}</CardText>;
    } else {
        content = (
            <ActiveFishGroupForm key={group.id} cageId={cageId} clientId={clientId} group={group} />
        );
    }

    return (
        <Card
            as="section"
            padding="xl"
            header={<CardHeader title={t('activeFishGroup.title')} level="h2" />}>
            {content}
        </Card>
    );
}
